import logger from "@/composables/logger"

// ExtractParams is a utility type that extracts all param names inside a route.path definition
// Param names must be wrapped in braces
//
// Ex: a route defined as /my-path/{parentId}/my-subpath/{id} contains two params => ['parentId', 'id']
type ExtractParams<Route> =
  Route extends `${string}{${infer Param}}${infer Rest}`
    ? Param | ExtractParams<Rest>
    : never

// RouteParams is a utility type that takes extracted param names from a route.path definition
// an convert them into a Record type for use as a function argument
//
// Ex: a route defined as /my-path/{parentId}/my-subpath/{id} resolves to a type of Record<"parentId" | "id": string | number>
type RouteParams<Route> = ExtractParams<Route> extends never
  ? undefined
  : Record<ExtractParams<Route>, string | number>

// A RouteMap is a static definition of available routes
// Each route is named as a key
type RoutesMap = {
  [name: string]: string
}

// RouteFunction determines the type of function that a given route should have
// When the route contains parameters the function requires an argument for hydrating the route
// Without parameters no arguments are required
type RouteFunction<R extends string> = ExtractParams<R> extends never
  ? () => string
  : (params: RouteParams<R>) => string

// RouterEntries is a utility type for iterating over a RouteMap and maintaining
// the key and value types when using utilities like Object.entries()
//
// Ex: (Object.entries(routes) as RouterEntries<R>).forEach(([key, route]) => {})
// key will be of type <keyof R> and route will be of type <R[keyof R]>
type RouterEntries<R extends RoutesMap> = {
  [K in keyof R]: [K, R[K]]
}[keyof R][]

// Router enforces the returned shape of the function createRouter
// It ensures that each named route requires the arguments necessary for hydrating
// the route.
//
// Ex: router.MySimpleRoute() vs router.MyParameterRoute({id: 10})
//
// NOTE: the implementation of createRouter does not need to satisfy this interface
// for the sake of the compiler, but should ensure runtime protections
type Router<R extends RoutesMap> = {
  [K in keyof R]: RouteFunction<R[K]>
}

// NOTE(spk): RegExp objects are stateful with the global or sticky flags
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/RegExp/test
// use caution if using a method like paramRegex.test()
const paramRegex = /{([a-zA-Z]+)}/g

// parseRoute takes a path and params argument for the purpose of constructing routes
// by replacing path params matching the key:value map in the second argument.
//
// This function is internal to the router package and should not be exported.
const parseRoute = (
  path: string,
  params: Record<string, string | number>
): string => {
  let replacements = 0
  const keyCount = Object.keys(params)?.length || 0

  const route = path.replace(paramRegex, (_, key) => {
    if (typeof params[key] === "undefined") {
      logger.report(
        `Failed to replace the {${key}} parameter in route: ${path}`,
        { path, param: `{${key}}` }
      )
      return `{${key}}` // leave the param unreplaced when it's not matched
    }

    replacements++
    return params[key].toString()
  })

  if (replacements !== keyCount) {
    logger.report(
      `The number of path params didn't match the number of replacements in route: ${path}`,
      { path, params }
    )
  }

  return route
}

// createRouter takes a RoutesMap and returns a Router
//
// This function skips explicit type safety and uses type assertions to avoid complex
// types and type gymnastics for the sake of fully supporting type inference.
//
// The burden is on the implementor and mainter to ensure that the type assertions are correct
// and the mapped route function matches the route type (with or without params).
export const createRouter = <R extends RoutesMap>(routes: R): Router<R> => {
  const router: Partial<Router<R>> = {}
  ;(Object.entries(routes) as RouterEntries<R>).forEach(([key, route]) => {
    let routerFunc
    if (route.match(paramRegex)) {
      routerFunc = (params: RouteParams<typeof route>) =>
        parseRoute(route, params)
    } else {
      routerFunc = () => {
        return route
      }
    }

    router[key] = routerFunc as RouteFunction<R[keyof R]>
  })

  return router as Router<R>
}
